import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Typography
} from "@mui/material";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {enqueueSnackbar} from "notistack";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {SingleSelect} from "../../../common/newSelect";
import {RightSidebarActionButton} from "../../../layout/rightSidebar/RightSidebarRail";
import {isAnswerableQuestion} from "../../shared/surveyAnswer.util";
import {SurveyEvaluationFilterState} from "../../shared/SurveyEvaluationFilters";
import {activeSurveyFilterLines, surveyFilterDescriptors} from "../../shared/surveyFilters";
import {buildSurveyEvaluationRequest} from "../../shared/useSurveyEvaluation";
import {useGuestCategories} from "../../../guestCategories/useGuestCategories";
import {downloadSurveyReport, surveyReportFallbackFileName, type SurveyReportFormat} from "./surveyReportDownload";

const formatRange = (range: {start: Date, end: Date}) => `${formatFullDMY(range.start)} – ${formatFullDMY(range.end)}`;

interface Props {
    /** Missing while the picked survey loads; the action stays on the rail, disabled. */
    survey?: Survey
    filters: SurveyEvaluationFilterState
    sectorOptions: Sector[]
    responseCount?: number
}

/**
 * The answers report as a rail action. The dialog says what goes into the file - the same survey,
 * period and filters as the page - and exports it in the picked format, like a list export. The API
 * builds and renders the file, and decides from the user's access whether the Insights sections
 * go in.
 */
function SurveyAnswersExportButton({survey, filters, sectorOptions, responseCount}: Props) {
    const {t, i18n} = useTranslation();
    const {categories: guestCategories} = useGuestCategories();
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState<SurveyReportFormat>('pdf');
    const [busy, setBusy] = useState(false);
    const [includeSensitive, setIncludeSensitive] = useState(false);

    const hasSensitiveQuestions = useMemo(
        () => (survey?.questions ?? []).some(question => (
            isAnswerableQuestion(question) && !!question.isSensitive
        )),
        [survey?.questions]
    );

    // The lines the report itself prints, so the dialog and the file cannot disagree.
    const filterLines = useMemo(
        () => activeSurveyFilterLines(surveyFilterDescriptors({
            languageOptions: survey?.languages ?? [],
            sectorOptions,
            guestCategories,
            currentVersion: survey?.version,
            t,
        }), filters),
        [survey?.languages, survey?.version, sectorOptions, guestCategories, filters, t]
    );

    const nothingToExport = responseCount === 0;

    const close = () => {
        if (!busy) setOpen(false);
    };

    const runExport = async () => {
        if (!survey?.id || nothingToExport || busy) return;

        setBusy(true);
        try {
            const saved = await downloadSurveyReport({
                // The API withholds sensitive answers unless asked, so the opt-in has to travel with the request.
                request: buildSurveyEvaluationRequest(survey.id, filters, includeSensitive && hasSensitiveQuestions),
                format,
                language: i18n.resolvedLanguage || i18n.language,
                fallbackFileName: surveyReportFallbackFileName(survey, filters, format),
            });

            // A refusal (its reason already shown) keeps the dialog open, so another format or a retry is one click.
            if (saved) setOpen(false);
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('Could not complete this action. Please try again.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setBusy(false);
        }
    };

    const summary = [
        {label: t('Survey'), value: survey?.name || t('Survey')},
        {label: t('Reporting period'), value: formatRange(filters.range)},
        ...(filters.compareRange ? [{label: t('Comparison period'), value: formatRange(filters.compareRange)}] : []),
        ...(filterLines.length ? filterLines : [{label: t('Filters'), value: t('All responses')}]),
        {label: t('Responses'), value: responseCount != null ? String(responseCount) : '—'},
        {
            label: t('Sensitive answers'),
            value: !hasSensitiveQuestions
                ? t('None in this survey')
                : includeSensitive ? t('Included in the file') : t('Not in the file'),
        },
    ];

    const formatOptions = [
        {value: 'pdf', label: t('PDF (.pdf)'), detail: t('Charts and scores, ready to print')},
        {value: 'docx', label: t('Word (.docx)'), detail: t('Same report, editable')},
        {value: 'xlsx', label: t('Excel (.xlsx)'), detail: t('Every count in a sheet')},
    ];

    return (
        <>
            <RightSidebarActionButton
                icon={<FileDownloadOutlinedIcon/>}
                label={t('Export')}
                disabled={!survey?.id || nothingToExport}
                onClick={() => setOpen(true)}
            />

            <Dialog open={open} onClose={close} fullWidth maxWidth={'xs'} aria-labelledby={'survey-answers-export-title'}>
                <DialogTitle id={'survey-answers-export-title'}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        {t('Export answers')}
                        <IconButton size={'small'} aria-label={t('Close')} disabled={busy} onClick={close}>
                            <CloseIcon fontSize={'small'}/>
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent>
                    <Stack gap={2.5}>
                        {/* What goes into the file: the page as it is filtered right now. */}
                        <Stack
                            gap={0.75}
                            sx={{border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 1.5}}
                        >
                            {summary.map(line => (
                                <Stack key={line.label} direction={'row'} justifyContent={'space-between'} gap={2}>
                                    <Typography variant={'body1'} color={'text.secondary'} sx={{flexShrink: 0}}>
                                        {line.label}
                                    </Typography>
                                    <Typography variant={'body1'} sx={{textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere'}}>
                                        {line.value}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>

                        <SingleSelect
                            label={t('Format')}
                            value={format}
                            options={formatOptions}
                            onChange={value => {
                                if (value === 'pdf' || value === 'docx' || value === 'xlsx') setFormat(value);
                            }}
                            disabled={busy}
                            disableClearable
                        />

                        {/* Only for a survey that has sensitive questions; the summary says when it has none. */}
                        {hasSensitiveQuestions && (
                            <FormControlLabel
                                disabled={busy}
                                control={(
                                    <Checkbox
                                        size={'small'}
                                        checked={includeSensitive}
                                        onChange={event => setIncludeSensitive(event.target.checked)}
                                    />
                                )}
                                label={(
                                    <Stack direction={'row'} alignItems={'center'} gap={0.75}>
                                        {/* The questions' lock, so the toggle reads as the marker it lifts. */}
                                        <LockOutlinedIcon aria-hidden sx={{fontSize: 20, color: 'warning.main'}}/>
                                        {t('Include sensitive answers')}
                                    </Stack>
                                )}
                                sx={{m: 0}}
                            />
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={close} disabled={busy}>{t('Cancel')}</Button>
                    <Button
                        variant={'contained'}
                        startIcon={busy ? <CircularProgress size={16} color={'inherit'}/> : <FileDownloadOutlinedIcon/>}
                        disabled={busy || !survey?.id || nothingToExport}
                        onClick={runExport}
                    >
                        {/* The button names what the file holds, as the list exports' button does. */}
                        {responseCount == null
                            ? t('Export')
                            : responseCount === 1
                                ? t('Export 1 response')
                                : t('Export {{count, number}} responses', {count: responseCount})}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default SurveyAnswersExportButton;
